import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { handleUnauthorized } from '../../TokenAuth/auth';

const initialState = {
  users: [],
  status: 'idle',
  error: null,
  pagination: {
    totalDocs: 0,
    totalPages: 1,
    page: 1,
    limit: 10,
  },
};

// Fetch Users
export const fetchUsers = createAsyncThunk(
  'users/fetchUsers',
  async ({ page, limit, search = '', startDate = '', endDate = '' }) => {
    const token = localStorage.getItem('vedhvidhi-admin-token');

    let url = `${process.env.REACT_APP_BASEURL}/admin/user?page=${page}&limit=${limit}&search=${encodeURIComponent(search)}`;
    if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
    if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;

    const response = await fetch(
      url,
      {
        method: 'GET',
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'application/json',
        },
      }
    );

    if (response.status === 401) {
      handleUnauthorized();
      throw new Error("Unauthorized: Please login again");
    }

    if (!response.ok) {
      throw new Error('Failed to fetch users');
    }

    const data = await response.json();
    return data;
  }
);

// Export users (no pagination)
export const exportUsers = createAsyncThunk(
  'users/exportUsers',
  async ({ search = '', startDate = '', endDate = '' }, { rejectWithValue }) => {
    try {
      const token = localStorage.getItem('vedhvidhi-admin-token');
      let url = `${process.env.REACT_APP_BASEURL}/admin/user?search=${encodeURIComponent(search)}`;
      if (startDate) url += `&startDate=${encodeURIComponent(startDate)}`;
      if (endDate) url += `&endDate=${encodeURIComponent(endDate)}`;

      const response = await fetch(url, {
        method: 'GET',
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'application/json',
        },
      });

      if (response.status === 401) {
        handleUnauthorized();
        throw new Error("Unauthorized: Please login again");
      }

      if (!response.ok) {
        throw new Error('Failed to export users');
      }

      const data = await response.json();
      return data;
    } catch (err) {
      return rejectWithValue(err.message || 'Export failed');
    }
  }
);

// Add User
export const addUser = createAsyncThunk(
  'users/addUser',
  async (userData) => {
    const token = localStorage.getItem('vedhvidhi-admin-token');

    const response = await fetch(
      `${process.env.REACT_APP_BASEURL}/admin/user`,
      {
        method: 'POST',
        headers: {
          Authorization: `${token}`,
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(userData),
      }
    );

    if (!response.ok) {
      throw new Error('Failed to add user');
    }

    const data = await response.json();
    return data;
  }
);

// Users Slice
const usersSlice = createSlice({
  name: 'users',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder

      // FETCH USERS
      .addCase(fetchUsers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchUsers.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users = action.payload.data || [];
        state.pagination = action.payload.paginationDetail || initialState.pagination;
      })
      .addCase(fetchUsers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })

      // ADD USER
      .addCase(addUser.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(addUser.fulfilled, (state, action) => {
        state.status = 'succeeded';
        state.users.unshift(action.payload);
      })
      .addCase(addUser.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      });
  },
});

export default usersSlice.reducer;

